import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { SlideChannelNormalizationResultSummary } from 'api/slideMultiplexChannelNormalizations';
import { compact, filter, flatMap, groupBy, map, orderBy, size, sortBy, uniq } from 'lodash';
import React, { useMemo } from 'react';

import { hasCompleteChannelNormalizationResults } from './normalizationInsights';

interface NormalizationCoverageEntry {
  slideId: string;
  channelKey: string;
  markerId: string;
  isNormalized: boolean;
}

export interface MissingNormalizationEntry {
  slideId: string;
  channelKey: string;
  markerId: string;
}

export interface MissingNormalizationSlideSummary {
  slideId: string;
  markerIds: string[];
  missingNormalizationCount: number;
}

export interface NormalizationCoverageSummary {
  selectedSlideCount: number;
  totalChannelSlideCount: number;
  normalizedChannelSlideCount: number;
  missingNormalizationEntries: MissingNormalizationEntry[];
  missingNormalizationSlideSummaries: MissingNormalizationSlideSummary[];
  isComplete: boolean;
  isAllMissing: boolean;
}

export interface UseNormalizationCoverageSummaryParams {
  normalizationSummaries?: SlideChannelNormalizationResultSummary[];
  selectedSlideIds?: string[];
  selectedMarkerIds?: string[];
}

export interface NormalizationCoverageSummaryPanelProps {
  coverageSummary: NormalizationCoverageSummary;
  getMarkerLabel: (markerId: string) => string;
  renderSlideCell: (args: { slideId: string; markerIdsToShow: string[] }) => React.ReactNode;
  title?: string;
  noCoverageMessage?: React.ReactNode;
  allAvailableMessage?: React.ReactNode;
  allMissingMessage?: React.ReactNode;
}

const markerQcTableStickyHeaderSx = {
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: 'background.paper',
  },
};

const formatCount = (count: number, singularLabel: string, pluralLabel = `${singularLabel}s`) =>
  `${count} ${count === 1 ? singularLabel : pluralLabel}`;

const buildCoverageEntries = ({
  normalizationSummaries,
  selectedSlideIds,
  selectedMarkerIds,
}: UseNormalizationCoverageSummaryParams): NormalizationCoverageEntry[] => {
  const selectedSlideIdSet = size(selectedSlideIds) ? new Set(selectedSlideIds) : null;
  const selectedMarkerIdSet = size(selectedMarkerIds) ? new Set(selectedMarkerIds) : null;
  const selectedSummaries = filter(
    normalizationSummaries || [],
    (normalizationSummary) => !selectedSlideIdSet || selectedSlideIdSet.has(normalizationSummary.slideId)
  );

  return compact(
    flatMap(selectedSummaries, (slideSummary) =>
      map(Object.entries(slideSummary.normalizationsSummary || {}), ([channelKey, normalizationValue]) => {
        const markerId = normalizationValue?.markerType;

        if (!markerId) {
          return null;
        }

        if (selectedMarkerIdSet && !selectedMarkerIdSet.has(markerId)) {
          return null;
        }

        return {
          slideId: slideSummary.slideId,
          channelKey,
          markerId,
          isNormalized: hasCompleteChannelNormalizationResults(normalizationValue.channelNormalizationResults),
        };
      })
    )
  );
};

export const buildNormalizationCoverageSummary = ({
  normalizationSummaries,
  selectedSlideIds,
  selectedMarkerIds,
}: UseNormalizationCoverageSummaryParams): NormalizationCoverageSummary => {
  const coverageEntries = buildCoverageEntries({ normalizationSummaries, selectedSlideIds, selectedMarkerIds });
  const fallbackSelectedSlideIds = uniq(map(normalizationSummaries || [], 'slideId'));
  const resolvedSelectedSlideIds = size(selectedSlideIds) ? selectedSlideIds || [] : fallbackSelectedSlideIds;
  const missingNormalizationEntries = orderBy(
    map(
      filter(coverageEntries, (coverageEntry) => !coverageEntry.isNormalized),
      ({ slideId, channelKey, markerId }) => ({ slideId, channelKey, markerId })
    ),
    ['slideId', 'markerId', 'channelKey'],
    ['asc', 'asc', 'asc']
  );
  const missingEntriesBySlideId = groupBy(missingNormalizationEntries, 'slideId');
  const missingNormalizationSlideSummaries = orderBy(
    map(missingEntriesBySlideId, (entries, slideId) => ({
      slideId,
      markerIds: sortBy(uniq(map(entries, 'markerId'))),
      missingNormalizationCount: entries.length,
    })),
    ['missingNormalizationCount', 'slideId'],
    ['desc', 'asc']
  );
  const totalChannelSlideCount = size(coverageEntries);
  const normalizedChannelSlideCount = size(filter(coverageEntries, 'isNormalized'));

  return {
    selectedSlideCount: size(resolvedSelectedSlideIds),
    totalChannelSlideCount,
    normalizedChannelSlideCount,
    missingNormalizationEntries,
    missingNormalizationSlideSummaries,
    isComplete: totalChannelSlideCount > 0 && size(missingNormalizationEntries) === 0,
    isAllMissing: totalChannelSlideCount > 0 && size(missingNormalizationEntries) === totalChannelSlideCount,
  };
};

export const useNormalizationCoverageSummary = ({
  normalizationSummaries,
  selectedSlideIds,
  selectedMarkerIds,
}: UseNormalizationCoverageSummaryParams) =>
  useMemo(
    () => buildNormalizationCoverageSummary({ normalizationSummaries, selectedSlideIds, selectedMarkerIds }),
    [normalizationSummaries, selectedMarkerIds, selectedSlideIds]
  );

export const NormalizationCoverageSummaryPanel: React.FC<NormalizationCoverageSummaryPanelProps> = ({
  coverageSummary,
  getMarkerLabel,
  renderSlideCell,
  title = 'Normalization coverage',
  noCoverageMessage,
  allAvailableMessage,
  allMissingMessage,
}) => {
  const {
    missingNormalizationEntries,
    missingNormalizationSlideSummaries,
    selectedSlideCount,
    totalChannelSlideCount,
    isAllMissing,
    isComplete,
  } = coverageSummary;
  const selectedSlideCountLabel = formatCount(selectedSlideCount, 'slide');
  const totalChannelSlideCountLabel = formatCount(totalChannelSlideCount, 'channel-slide pair');
  const missingSlideCountLabel = formatCount(missingNormalizationSlideSummaries.length, 'slide');
  const missingNormalizationCountLabel = formatCount(missingNormalizationEntries.length, 'marker normalization');

  if (!totalChannelSlideCount) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {title}
        </Typography>
        <Alert severity="warning" variant="outlined">
          {noCoverageMessage || 'Normalization coverage could not be verified for the selected slides.'}
        </Alert>
      </Box>
    );
  }

  if (isComplete) {
    return (
      <Box sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            {allAvailableMessage ||
              `All normalization values are available across ${totalChannelSlideCountLabel} on ${selectedSlideCountLabel}.`}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Alert severity={isAllMissing ? 'error' : 'warning'} variant="outlined" sx={{ mb: 1.5 }}>
        {isAllMissing
          ? allMissingMessage ||
            `All normalization values are missing across ${totalChannelSlideCountLabel} on ${selectedSlideCountLabel}.`
          : `${missingSlideCountLabel} ${
              missingNormalizationSlideSummaries.length === 1 ? 'is' : 'are'
            } missing normalization values across ${missingNormalizationCountLabel}.`}
      </Alert>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer
          sx={{
            maxHeight: 260,
            ...markerQcTableStickyHeaderSx,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Slide</TableCell>
                <TableCell align="right">Missing marker normalizations</TableCell>
                <TableCell>Markers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(missingNormalizationSlideSummaries, (summary) => (
                <TableRow key={summary.slideId} hover>
                  <TableCell>
                    {renderSlideCell({
                      slideId: summary.slideId,
                      markerIdsToShow: summary.markerIds,
                    })}
                  </TableCell>
                  <TableCell align="right">{summary.missingNormalizationCount}</TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {map(summary.markerIds, (markerId) => (
                        <Chip key={`${summary.slideId}-${markerId}`} size="small" label={getMarkerLabel(markerId)} />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
